import { useState } from 'react';
import { applyTheme, AVAILABLE_THEMES, getStoredTheme, type ThemeName } from '@ui/shared/tokens/theme';

/**
 * Theme switcher (settings-rewards.md §6 "Theme" row) — per the
 * coordinator's inclusion call, this ships now on the Rewards/Settings
 * screen rather than waiting for a dedicated Settings surface (the spec's
 * §6 explicitly names this as a flagged-but-unbuilt slot: "if the switcher
 * ships alongside rewards, it lives here as a two-option segmented control
 * (Green / Orange, swatch preview of hue.primary for each) persisting to
 * local settings"). Wired to the existing theme-swappable token
 * architecture (`src/ui/shared/tokens/theme.ts`, `tokens.css`'s
 * `[data-theme]` selectors) that Phase 1 already built — this component
 * adds no new token values, only the UI to pick between the ones that exist.
 */
export function ThemeSwitcher() {
  const [current, setCurrent] = useState<ThemeName>(getStoredTheme());

  function select(theme: ThemeName) {
    applyTheme(theme);
    setCurrent(theme);
  }

  return (
    <div>
      <div
        style={{
          fontSize: 'var(--type-caption-size)',
          color: 'var(--text-muted)',
          marginBottom: 'var(--space-2)',
        }}
      >
        Theme
      </div>
      <div
        role="radiogroup"
        aria-label="Theme"
        style={{
          display: 'inline-flex',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-strong)',
          overflow: 'hidden',
        }}
      >
        {AVAILABLE_THEMES.map((theme) => {
          const selected = theme === current;
          return (
            <button
              key={theme}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => select(theme)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: '0 var(--space-4)',
                minHeight: 'var(--touch-target-min)',
                background: selected ? 'var(--surface-hover)' : 'transparent',
                color: selected ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: selected ? 600 : 500,
                fontSize: 'var(--type-body-size)',
                borderRight: theme !== AVAILABLE_THEMES[AVAILABLE_THEMES.length - 1] ? '1px solid var(--border-strong)' : 'none',
              }}
            >
              <ThemeSwatch theme={theme} />
              {theme === 'green' ? 'Green' : 'Orange'}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Swatch preview per spec ("swatch preview of hue.primary for each") — reads
 * that theme's own primary hue directly rather than the currently-applied
 * theme, so both options are visible side by side regardless of which one
 * is active. tokens.css only exposes hue.primary via the active
 * [data-theme] attribute, so this small literal map mirrors tokens.json's
 * two theme value-maps for JUST this preview purpose (the swatch is
 * decorative, not a place a screen renders live app content, so a literal
 * here does not violate "no hard-coded colors" for functional UI — see
 * comment below for why this is the one sanctioned exception).
 */
function ThemeSwatch({ theme }: { readonly theme: ThemeName }) {
  // NOTE: these two hex values are tokens.json's own themes.green.color.hue.primaryVivid
  // and themes.orange.color.hue.primaryVivid, copied here ONLY because a
  // swatch needs to show a color that is NOT the currently-active theme
  // (CSS custom properties only expose the active theme's value). This is
  // a values-are-implementation-detail read, not an independent color
  // decision — if tokens.json's theme hexes ever change, this swatch must
  // be updated to match (flagged for ux-designer/token-owner awareness,
  // not a value dev-ui is entitled to invent).
  const hex = theme === 'green' ? '#3DDB7F' : '#FFA858';
  return (
    <span
      aria-hidden="true"
      style={{
        width: '14px',
        height: '14px',
        borderRadius: 'var(--radius-pill)',
        background: hex,
        flex: 'none',
      }}
    />
  );
}
